import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const SearchStateContext = createContext();
const STORAGE_KEY = 'zahoot_search_state';
const PAGE_LOAD_KEY = 'zahoot_page_load_timestamp';
const NAVIGATION_TYPE_KEY = 'zahoot_navigation_type';

export const useSearchState = () => useContext(SearchStateContext);

export const SearchStateProvider = ({ children }) => {
  // Flag to track if we've already loaded from storage
  const initializedRef = useRef(false);
  
  // Try to load initial state from sessionStorage
  const loadInitialState = () => {
    try {
      // Check if this is a page reload by comparing timestamps
      const lastLoadTime = sessionStorage.getItem(PAGE_LOAD_KEY);
      const currentTime = Date.now().toString();
      sessionStorage.setItem(PAGE_LOAD_KEY, currentTime);
      
      // Get the saved state from sessionStorage
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      if (savedState) {
        // On page refresh or navigation, keep the state instead of clearing it
        initializedRef.current = true;
        const parsedState = JSON.parse(savedState);
        
        // Check if this is a back/forward navigation
        const navigationType = sessionStorage.getItem(NAVIGATION_TYPE_KEY);
        const isBackNavigation = navigationType === 'back';
        
        // If this is a refresh (< 1 second between loads), make sure we keep the state
        if (lastLoadTime && (currentTime - lastLoadTime) < 1000) {
          console.log('Page refresh detected, preserving search state');
          // Set fromLawyerProfile to true to ensure state is valid on refresh
          return {
            ...parsedState,
            fromLawyerProfile: true,
            isRefreshedState: true,
            isBackNavigation: false,
            shouldAutoExecute: true, // Mark for auto-execution
            timestamp: Date.now() // Refresh the timestamp so it doesn't expire
          };
        }
        
        // If this was a back navigation, mark it
        if (isBackNavigation) {
          console.log('Back navigation detected, preserving search state');
          return {
            ...parsedState,
            isBackNavigation: true,
            shouldAutoExecute: true, // Mark for auto-execution
            timestamp: Date.now() // Refresh the timestamp
          };
        }
        
        return parsedState;
      }
    } catch (error) {
      console.error('Error loading search state:', error);
    }
    
    // Default state if nothing in storage
    return {
      // Search type tracking
      searchType: null, // 'standard', 'quick', 'ai'
      
      // Standard search criteria
      searchCriteria: {
        service: null,
        area: null,
        specific: null
      },
      
      // Fuzzy/Quick search criteria
      fuzzySearchCriteria: {
        name: '',
        practiceAreas: [],
        organisationType: '',
        sraNumber: '',
        address: '',
        phone: '',
        email: ''
      },
      
      // AI search criteria
      aiSearchCriteria: {
        query: '',
        originalQuery: ''
      },
      
      // Search state
      fuzzySearchEnabled: false,
      shouldPerformSearch: false,
      shouldAutoExecute: false,
      totalResults: 0,
      currentPage: 1,
      executedAt: null,
      
      // Navigation state
      fromLawyerProfile: false,
      isRefreshedState: false,
      isBackNavigation: false,
      timestamp: null
    };
  };

  // State for search results and filters
  const [searchState, setSearchState] = useState(loadInitialState);

  // Set up listener for back/forward navigation
  useEffect(() => {
    // Listen for popstate events (back/forward navigation)
    const handlePopState = () => {
      console.log('Back/forward navigation detected');
      sessionStorage.setItem(NAVIGATION_TYPE_KEY, 'back');
      
      // Get the current state
      const savedState = sessionStorage.getItem(STORAGE_KEY);
      if (savedState) {
        // Parse and update the state to mark it as a back navigation
        const parsedState = JSON.parse(savedState);
        const updatedState = {
          ...parsedState,
          isBackNavigation: true,
          shouldAutoExecute: true,
          timestamp: Date.now()
        };
        
        // Save the updated state
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedState));
        setSearchState(updatedState);
      }
    };
    
    // Set initial navigation type
    sessionStorage.setItem(NAVIGATION_TYPE_KEY, 'direct');
    
    // Add event listener for popstate (back/forward navigation)
    window.addEventListener('popstate', handlePopState);
    
    // Remove listener on cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Ensure an object has valid label and value properties
  const normalizeOption = (option) => {
    if (!option) return null;
    
    // If it's already an object with label and value, return it
    if (typeof option === 'object' && option !== null) {
      // Make sure it has both label and value
      return {
        label: option.label || option.value || '',
        value: option.value || option.label || ''
      };
    }
    
    // If it's a string, create an object
    if (typeof option === 'string') {
      return {
        label: option,
        value: option
      };
    }
    
    return null;
  };

  // Save the normalized state to sessionStorage
  const saveToSessionStorage = (state) => {
    try {
      console.log('Saving to sessionStorage:', JSON.stringify(state));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      // Set navigation type to direct since we're saving explicitly
      sessionStorage.setItem(NAVIGATION_TYPE_KEY, 'direct');
    } catch (error) {
      console.error('Error saving search state to session storage:', error);
    }
  };

  // Enhanced function to save search state for any search type
  const saveSearchState = (state) => {
    // Make sure we handle undefined values
    if (!state) {
      console.warn('Attempted to save undefined search state');
      return;
    }
    
    // Determine search type if not provided
    let searchType = state.searchType;
    if (!searchType) {
      if (state.aiQuery || state.aiSearchCriteria?.query) {
        searchType = 'ai';
      } else if (state.fuzzySearchEnabled || state.fuzzySearchCriteria) {
        searchType = 'quick';
      } else {
        searchType = 'standard';
      }
    }
    
    // Normalize the service, area, and specific options
    const service = normalizeOption(state.selectedService);
    const area = normalizeOption(state.selectedArea);
    const specific = normalizeOption(state.selectedSpecific);
    
    console.log('Saving search state with values:', {
      searchType,
      service: service?.label,
      area: area?.label,
      specific: specific?.label,
      fuzzySearchEnabled: state.fuzzySearchEnabled || false,
      fuzzySearchCriteria: state.fuzzySearchCriteria || {},
      aiQuery: state.aiQuery
    });
    
    const newState = {
      // Search type
      searchType,
      
      // Standard search criteria
      searchCriteria: {
        service,
        area,
        specific
      },
      
      // Fuzzy search criteria
      fuzzySearchCriteria: state.fuzzySearchCriteria || searchState.fuzzySearchCriteria || {},
      fuzzySearchEnabled: state.fuzzySearchEnabled || false,
      
      // AI search criteria
      aiSearchCriteria: {
        query: state.aiQuery || state.aiSearchCriteria?.query || '',
        originalQuery: state.originalQuery || state.aiSearchCriteria?.originalQuery || state.aiQuery || ''
      },
      
      // Search state
      shouldPerformSearch: true,
      shouldAutoExecute: false, // Will be set to true on page refresh
      totalResults: state.totalResults || 0,
      currentPage: state.currentPage || 1,
      executedAt: Date.now(),
      
      // Navigation state
      fromLawyerProfile: true,
      isRefreshedState: state.isRefreshedState || false,
      isBackNavigation: state.isBackNavigation || false,
      timestamp: Date.now()
    };
    
    console.log('Saving normalized search state:', JSON.stringify(newState));
    setSearchState(newState);
    saveToSessionStorage(newState);
  };

  // Function to save AI search criteria
  const saveAiSearchState = (query, originalQuery = null) => {
    setSearchState(prevState => {
      const updatedState = {
        ...prevState,
        searchType: 'ai',
        aiSearchCriteria: {
          query: query,
          originalQuery: originalQuery || query
        },
        shouldPerformSearch: true,
        executedAt: Date.now(),
        timestamp: Date.now()
      };
      
      saveToSessionStorage(updatedState);
      return updatedState;
    });
  };

  // Function to save standard search criteria
  const saveStandardSearchState = (service, area, specific = null) => {
    setSearchState(prevState => {
      const normalizedService = normalizeOption(service);
      const normalizedArea = normalizeOption(area);
      const normalizedSpecific = normalizeOption(specific);
      
      const updatedState = {
        ...prevState,
        searchType: 'standard',
        searchCriteria: {
          service: normalizedService,
          area: normalizedArea,
          specific: normalizedSpecific
        },
        shouldPerformSearch: true,
        executedAt: Date.now(),
        timestamp: Date.now()
      };
      
      saveToSessionStorage(updatedState);
      return updatedState;
    });
  };

  // Function to save fuzzy search criteria
  const saveFuzzySearchCriteria = (criteria, enabled) => {
    setSearchState(prevState => {
      const updatedState = {
        ...prevState,
        searchType: 'quick',
        fuzzySearchCriteria: criteria,
        fuzzySearchEnabled: enabled,
        shouldPerformSearch: true,
        executedAt: Date.now(),
        timestamp: Date.now()
      };
      
      saveToSessionStorage(updatedState);
      return updatedState;
    });
  };

  // Function to toggle fuzzy search
  const toggleFuzzySearch = (enabled) => {
    setSearchState(prevState => {
      const updatedState = {
        ...prevState,
        fuzzySearchEnabled: enabled,
        timestamp: Date.now()
      };
      
      saveToSessionStorage(updatedState);
      return updatedState;
    });
  };

  // Function to check if AI search should auto-execute (credit-aware)
  const shouldAutoExecuteAiSearch = () => {
    if (searchState.searchType !== 'ai' || !searchState.shouldAutoExecute) {
      return false;
    }
    
    // Auto-execute if search was performed recently (within 5 minutes) to preserve credits
    const FRESH_SEARCH_WINDOW = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    
    if (searchState.executedAt && (now - searchState.executedAt) < FRESH_SEARCH_WINDOW) {
      console.log('AI search is fresh, auto-executing without credit deduction');
      return true;
    }
    
    console.log('AI search is stale, will require user confirmation');
    return false;
  };

  // Function to mark auto-execution as complete
  const markAutoExecutionComplete = () => {
    setSearchState(prevState => ({
      ...prevState,
      shouldAutoExecute: false
    }));
  };

  // Function to clear search state
  const clearSearchState = () => {
    const emptyState = {
      searchType: null,
      searchCriteria: {
        service: null,
        area: null,
        specific: null
      },
      fuzzySearchCriteria: {
        name: '',
        practiceAreas: [],
        organisationType: '',
        sraNumber: '',
        address: '',
        phone: '',
        email: ''
      },
      aiSearchCriteria: {
        query: '',
        originalQuery: ''
      },
      fuzzySearchEnabled: false,
      shouldPerformSearch: false,
      shouldAutoExecute: false,
      totalResults: 0,
      currentPage: 1,
      executedAt: null,
      fromLawyerProfile: false,
      isRefreshedState: false,
      isBackNavigation: false,
      timestamp: null
    };
    
    setSearchState(emptyState);
    
    try {
      // Also clear from sessionStorage
      sessionStorage.removeItem(STORAGE_KEY);
      // Reset navigation type
      sessionStorage.setItem(NAVIGATION_TYPE_KEY, 'direct');
    } catch (error) {
      console.error('Error clearing search state from session storage:', error);
    }
  };

  // Check if state is still valid (not expired)
  const isStateValid = () => {
    // First check the in-memory state
    let currentState = searchState;
    
    // Make sure we have a searchCriteria object that's properly structured
    if (!currentState || !currentState.timestamp || 
       (!currentState.fromLawyerProfile && !currentState.isRefreshedState && !currentState.isBackNavigation) ||
       !currentState.searchCriteria) {
      try {
        const savedState = sessionStorage.getItem(STORAGE_KEY);
        if (savedState) {
          currentState = JSON.parse(savedState);
          // Ensure we have required fields with proper initialization
          if (!currentState.searchCriteria) {
            currentState.searchCriteria = { service: null, area: null, specific: null };
          }
          
          if (!currentState.fuzzySearchCriteria) {
            currentState.fuzzySearchCriteria = {
              name: '',
              practiceAreas: [],
              organisationType: '',
              sraNumber: '',
              address: '',
              phone: '',
              email: ''
            };
          }
          
          // Only update in-memory state if we found valid state in storage
          // and it's different from what we already have
          if (currentState.timestamp && 
             (currentState.fromLawyerProfile || currentState.isRefreshedState || currentState.isBackNavigation)) {
            setSearchState(prev => {
              // Only update if different to avoid render loops
              if (JSON.stringify(prev) !== JSON.stringify(currentState)) {
                return currentState;
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error('Error reading search state from session storage:', error);
        return false;
      }
    }
    
    // State is valid if it exists, has a timestamp, and is from lawyer profile, a refresh, or back navigation
    if (!currentState || !currentState.timestamp || 
       (!currentState.fromLawyerProfile && !currentState.isRefreshedState && !currentState.isBackNavigation)) {
      return false;
    }

    // State expires after 30 minutes (1800000 ms) instead of 5 minutes
    const EXPIRY_TIME = 1800000;
    const now = Date.now();
    return (now - currentState.timestamp) < EXPIRY_TIME;
  };

  // Function to mark search as performed
  const markSearchPerformed = () => {
    setSearchState(prevState => {
      const updatedState = {
        ...prevState,
        shouldPerformSearch: false
      };
      
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedState));
      } catch (error) {
        console.error('Error updating search state in session storage:', error);
      }
      
      return updatedState;
    });
  };

  return (
    <SearchStateContext.Provider value={{ 
      searchState, 
      saveSearchState, 
      saveAiSearchState,
      saveStandardSearchState,
      clearSearchState,
      markSearchPerformed,
      isStateValid,
      toggleFuzzySearch,
      saveFuzzySearchCriteria,
      shouldAutoExecuteAiSearch,
      markAutoExecutionComplete
    }}>
      {children}
    </SearchStateContext.Provider>
  );
};

export default SearchStateContext; 